import { useEffect, useState } from "react";

/**
 * Tracks browser connectivity.
 *
 * `navigator.onLine` is a weak signal — it reports whether a network interface exists, not
 * whether the internet is reachable, so it can read `true` behind a captive portal. It is still
 * the right primitive here: the goal is to explain the obvious case (laptop lid closed, wifi
 * dropped) rather than to prove reachability, and anything stronger means polling a health
 * endpoint on a timer, which costs requests for a rare event.
 *
 * Initialised from `navigator.onLine` rather than defaulting to true, so a page loaded while
 * already offline shows the banner immediately instead of waiting for an event that never fires.
 */
export function useOnlineStatus(): boolean {
  const [online, setOnline] = useState(() =>
    typeof navigator === "undefined" ? true : navigator.onLine,
  );

  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  return online;
}
